import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { supabase } from '../../supabase';
import SchoolSidebar from './SchoolSidebar';
import './SchoolDashboard.css';

function EditVehicle() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [schoolData, setSchoolData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingVehicle, setLoadingVehicle] = useState(true);
  const [transportPersons, setTransportPersons] = useState([]);
  const [formData, setFormData] = useState({
    bus_number: '',
    capacity: '',
    rules_policies: '',
    driver_id: ''
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [success, setSuccess] = useState('');

  useEffect(() => {
    const sd = localStorage.getItem('schoolData');
    if (!sd) {
      navigate('/schoollogin');
      return;
    }
    setSchoolData(JSON.parse(sd));
    setLoading(false);
  }, [navigate]);

  useEffect(() => {
    if (schoolData?.id) {
      fetchTransportPersons();
    }
  }, [schoolData?.id]);

  useEffect(() => {
    if (id && schoolData?.id) {
      fetchVehicleData();
    }
  }, [id, schoolData?.id]);

  // Update driver_id when transportPersons are loaded and we have vehicle data
  useEffect(() => {
    if (formData.bus_number && transportPersons.length > 0 && !formData.driver_id) {
      // Try to fetch vehicle again to match driver
      const matchDriver = async () => {
        const { data: vehicleData } = await supabase
          .from('transport_vehicles')
          .select('driver_name, driver_phone')
          .eq('id', id)
          .single();
        
        if (vehicleData && vehicleData.driver_name) {
          const matchingDriver = transportPersons.find(
            tp => tp.name === vehicleData.driver_name && (tp.phone === vehicleData.driver_phone || !vehicleData.driver_phone)
          );
          if (matchingDriver) {
            setFormData(prev => ({
              ...prev,
              driver_id: matchingDriver.id
            }));
          }
        }
      };
      matchDriver();
    }
  }, [transportPersons, id]);

  const fetchVehicleData = async () => {
    try {
      setLoadingVehicle(true);
      const { data, error } = await supabase
        .from('transport_vehicles')
        .select('*')
        .eq('id', id)
        .eq('school_id', schoolData.id)
        .single();

      if (error) {
        console.error('Error fetching vehicle:', error);
        alert('Error loading vehicle: ' + error.message);
        navigate('/school/vehicle');
        return;
      }

      if (data) {
        // Find the driver_id by matching driver_name and driver_phone
        let driverId = '';
        if (data.driver_name && transportPersons.length > 0) {
          const matchingDriver = transportPersons.find(
            tp => tp.name === data.driver_name && (tp.phone === data.driver_phone || !data.driver_phone)
          );
          if (matchingDriver) {
            driverId = matchingDriver.id;
          }
        }

        setFormData({
          bus_number: data.bus_number || '',
          capacity: data.capacity || '',
          rules_policies: data.rules_policies || '',
          driver_id: driverId
        });
      }
    } catch (err) {
      console.error('Error:', err);
      alert('An error occurred while loading the vehicle.');
      navigate('/school/vehicle');
    } finally {
      setLoadingVehicle(false);
    }
  };

  const fetchTransportPersons = async () => {
    try {
      const { data, error } = await supabase
        .from('transport_persons')
        .select('id, name, email, phone')
        .eq('school_id', schoolData.id)
        .eq('is_active', true)
        .order('name', { ascending: true });

      if (error) {
        console.error('Error fetching transport persons:', error);
        return;
      }

      setTransportPersons(data || []);
    } catch (err) {
      console.error('Error:', err);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
    
    // Clear success message
    if (success) {
      setSuccess('');
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.bus_number.trim()) {
      newErrors.bus_number = 'Vehicle number is required';
    }
    
    if (!formData.capacity || parseInt(formData.capacity) <= 0) {
      newErrors.capacity = 'Number of seats must be greater than 0';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    setSuccess('');
    
    try {
      // Get driver details if selected
      let driver_name = '';
      let driver_phone = '';
      
      if (formData.driver_id) {
        const selectedDriver = transportPersons.find(tp => tp.id === formData.driver_id);
        if (selectedDriver) {
          driver_name = selectedDriver.name || '';
          driver_phone = selectedDriver.phone || '';
        }
      }
      
      const vehicleData = {
        bus_number: formData.bus_number.trim(),
        capacity: parseInt(formData.capacity, 10),
        available_seats: parseInt(formData.capacity, 10),
        driver_name: driver_name || null,
        driver_phone: driver_phone || null,
        rules_policies: formData.rules_policies.trim() || null,
        updated_at: new Date().toISOString()
      };
      
      const { error } = await supabase
        .from('transport_vehicles')
        .update(vehicleData)
        .eq('id', id);
      
      if (error) {
        console.error('Error updating vehicle:', error);
        alert('Error updating vehicle: ' + error.message);
        return;
      }
      
      setSuccess('Vehicle updated successfully!');
      
      // Navigate back to vehicles list after a short delay
      setTimeout(() => {
        navigate('/school/vehicle');
      }, 1500);
      
    } catch (err) {
      console.error('Error:', err);
      alert('An error occurred while updating the vehicle. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleLogout = () => navigate('/schoollogin');

  if (loading || loadingVehicle) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div className="school-dashboard">
      <SchoolSidebar schoolData={schoolData} onLogout={handleLogout} />
      
      <div className="main-content">
        <div className="content-header">
          <h1>Edit Vehicle</h1>
        </div>
        
        <div className="content-body">
          <div className="add-school-content">
            <div className="school-form-container">
              <form onSubmit={handleSubmit} className="school-form">
                <div className="form-section">
                  <h3>Vehicle Information</h3>
                  
                  <div className="form-group">
                    <label htmlFor="bus_number">
                      Vehicle Number <span className="asterisk">*</span>
                    </label>
                    <input
                      type="text"
                      id="bus_number"
                      name="bus_number"
                      value={formData.bus_number}
                      onChange={handleInputChange}
                      placeholder="Enter vehicle number (e.g., BUS-001)"
                      className={errors.bus_number ? 'error' : ''}
                      required
                    />
                    {errors.bus_number && (
                      <span className="error-text">{errors.bus_number}</span>
                    )}
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="capacity">
                      Number of Seats <span className="asterisk">*</span>
                    </label>
                    <input
                      type="number"
                      id="capacity"
                      name="capacity"
                      value={formData.capacity}
                      onChange={handleInputChange}
                      placeholder="Enter number of seats"
                      min="1"
                      className={errors.capacity ? 'error' : ''}
                      required
                    />
                    {errors.capacity && (
                      <span className="error-text">{errors.capacity}</span>
                    )}
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="driver_id">Select Driver</label>
                    <select
                      id="driver_id"
                      name="driver_id"
                      value={formData.driver_id}
                      onChange={handleInputChange}
                    >
                      <option value="">-- Select a driver --</option>
                      {transportPersons.map((person) => (
                        <option key={person.id} value={person.id}>
                          {person.name} {person.email ? `(${person.email})` : ''}
                        </option>
                      ))}
                    </select>
                    {transportPersons.length === 0 && (
                      <span className="error-text" style={{ color: '#6B7280', fontSize: '13px' }}>
                        No transport persons available. Please add a transport person first.
                      </span>
                    )}
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="rules_policies">Bus Rules and Policies</label>
                    <textarea
                      id="rules_policies"
                      name="rules_policies"
                      value={formData.rules_policies}
                      onChange={handleInputChange}
                      placeholder="Enter bus rules and policies (optional)"
                      rows="6"
                    />
                  </div>
                </div>
                
                {success && (
                  <div className="success-message" style={{ 
                    backgroundColor: '#D1FAE5', 
                    color: '#065F46', 
                    padding: '12px 16px', 
                    borderRadius: '8px', 
                    marginBottom: '24px',
                    border: '1px solid #A7F3D0'
                  }}>
                    {success}
                  </div>
                )}
                
                <div className="form-actions">
                  <button 
                    type="button" 
                    className="cancel-btn" 
                    onClick={() => navigate('/school/vehicle')}
                  >
                    Cancel
                  </button>
                  <button 
                    type="submit" 
                    className="submit-btn" 
                    disabled={isSubmitting}
                    style={{ backgroundColor: '#FF8C42', color: 'white' }}
                  >
                    {isSubmitting ? 'Updating Vehicle...' : 'Update Vehicle'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default EditVehicle;

